import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { trackEvent, trackPageView } from '../utils/analytics';

const CLICKABLE_SELECTOR = [
  '[data-analytics-id]',
  'button',
  'a[href]',
  'input[type="button"]',
  'input[type="submit"]',
  'input[type="checkbox"]',
  'input[type="radio"]',
  'label',
  'select',
  '[role="button"]',
].join(',');

function cleanText(value, maxLength = 120) {
  if (!value) return undefined;
  const text = String(value).replace(/\s+/g, ' ').trim();
  return text ? text.slice(0, maxLength) : undefined;
}

function getAssociatedLabel(element) {
  if (!element) return undefined;

  const wrappingLabel = element.closest && element.closest('label');
  if (wrappingLabel) return cleanText(wrappingLabel.innerText);

  if (element.id && typeof document !== 'undefined') {
    try {
      const label = document.querySelector(`label[for="${element.id}"]`);
      if (label) return cleanText(label.innerText);
    } catch {
      return undefined;
    }
  }

  return undefined;
}

function getSafeHref(element) {
  const href = element.getAttribute('href');
  if (!href) return undefined;

  try {
    const url = new URL(href, window.location.origin);
    return url.origin === window.location.origin ? url.pathname : `${url.origin}${url.pathname}`;
  } catch {
    return cleanText(href, 300);
  }
}

function getElementLabel(element) {
  const explicitLabel =
    element.getAttribute('data-analytics-label') ||
    element.getAttribute('aria-label') ||
    element.getAttribute('title');

  if (explicitLabel) return cleanText(explicitLabel);

  const tagName = element.tagName.toLowerCase();
  const inputType = element.getAttribute('type');

  if (tagName === 'input') {
    if (inputType === 'button' || inputType === 'submit') {
      return cleanText(element.getAttribute('value')) || cleanText(element.name) || cleanText(element.id);
    }
    return getAssociatedLabel(element) || cleanText(element.name) || cleanText(element.id) || inputType;
  }

  if (tagName === 'select') {
    return getAssociatedLabel(element) || cleanText(element.name) || cleanText(element.id) || 'select';
  }

  return cleanText(element.innerText) || cleanText(element.name) || cleanText(element.id) || tagName;
}

function getClassNames(element) {
  return Array.from(element.classList || [])
    .filter(Boolean)
    .slice(0, 6);
}

function getAnalyticsElement(target) {
  if (!target || !target.closest) return null;
  const element = target.closest(CLICKABLE_SELECTOR);
  if (!element || element.closest('[data-analytics-ignore="true"]')) return null;
  return element;
}

function AnalyticsTracker() {
  const lastClickRef = useRef({ key: '', time: 0 });
  const location = useLocation();

  useEffect(() => {
    trackPageView(location.pathname, {
      search_length: location.search ? location.search.length : 0,
    });
  }, [location.pathname, location.search]);

  useEffect(() => {
    const handleClick = (event) => {
      const element = getAnalyticsElement(event.target);
      if (!element) return;

      const tagName = element.tagName.toLowerCase();
      const type = element.getAttribute('type') || undefined;
      const target = getElementLabel(element);
      const dedupeKey = `${window.location.pathname}:${target || tagName}:${type || ''}`;
      const now = Date.now();

      if (lastClickRef.current.key === dedupeKey && now - lastClickRef.current.time < 250) {
        return;
      }
      lastClickRef.current = { key: dedupeKey, time: now };

      trackEvent('ui_click', {
        target: target || tagName,
        properties: {
          tag: tagName,
          type,
          id: cleanText(element.id, 80),
          class_names: getClassNames(element),
          href: tagName === 'a' ? getSafeHref(element) : undefined,
          checked: tagName === 'input' && ['checkbox', 'radio'].includes(type)
            ? Boolean(element.checked)
            : undefined,
        },
      });
    };

    document.addEventListener('click', handleClick, true);
    return () => document.removeEventListener('click', handleClick, true);
  }, []);

  return null;
}

export default AnalyticsTracker;
